import { injectService } from '@patsnap/tahiti-core/ioc/inject';
import { SpecificationEmbodimentOutlineService } from '@/domains/specification/services/specification-embodiment-outline-service';
import { computed, ref, watch } from 'vue';
import { createSharedComposable } from '@vueuse/core';
import type { EmbodimentOutline } from '@/domains/specification/apis/create-embodiment-outline-api';
import { useClaimTree } from './use-claim-tree';
import type { SpecificationConfirmClaimItem } from '@/domains/specification/types/specification-confirm-claims';
import _ from 'lodash';
import { SpecificationEmbodimentOutlineModel } from '@/domains/specification/models/specification-embodiment-outline-model';
import { useDisposes } from '@telecom/shared/composables/use-disposes';

export const useSpecificationEmbodimentOutlines = () => {
    const model = injectService<SpecificationEmbodimentOutlineModel>(SpecificationEmbodimentOutlineModel);
    const { disposes } = useDisposes();

    const state = ref(model.modelData);
    disposes.push(
        model.onStatesChange((_state) => {
            state.value = _state;
        }),
    );

    const outlines = computed(() => state.value.outlines);

    const nextEmbodimentNumber = computed(() => {
        return outlines.value.reduce((max, outline) => {
            return Math.max(max, outline.embodiment_number || 0);
        }, 0) + 1;
    });

    const createConfigRule = () => {
        return {
            embodiment_number: `${nextEmbodimentNumber.value}`,
            type: '',
            claims: [],
        };
    };

    return {
        state,
        model,
        outlines,
        nextEmbodimentNumber,
        createConfigRule,
    };
};

export const useSpecificationEmbodimentConfigRuleTransform = () => {
    const { claimTreeNumMap } = useClaimTree();

    /**
     * 将 EmbodimentOutline 转换为 EmbodimentOutlineConfigRule
     *
     * EmbodimentOutline 是后端API返回的数据结构
     * EmbodimentOutlineConfigRule 是前端UI使用的管理配置
     */
    const outline2ConfigRule = (outline: EmbodimentOutline) => {
        const claimNums = outline.claim_numbers || [];
        const claims = claimNums.map(num => claimTreeNumMap.value[`${num}`] || null);
        const filteredClaims = claims.filter(Boolean) as SpecificationConfirmClaimItem[];
        return {
            embodiment_number: `${outline.embodiment_number}` || '',
            type: outline.type || '',
            claims: filteredClaims,
        };
    };

    const joinClaimText = (claims: SpecificationConfirmClaimItem[]) => {
        return claims.reduce((acc, claim) => {
            const claimFeatures = (claim.claimFeatures || []).reduce((acc, feature) => {
                return `${acc}${feature.feature || ''}\n`;
            }, '');
            return `${acc}${claim.num}. ${claim.simpleClaim || ''}\n${claimFeatures}\n`;
        }, '');
    };

    const configRule2Outline = (configRule: EmbodimentOutlineConfigRule) => {
        return {
            claim_numbers: configRule.claims.map(claim => _.toNumber(claim.num)),
            claim_text: joinClaimText(configRule.claims),
            type: configRule.type,
            label: 'outline',
        } as EmbodimentOutline;
    };

    return {
        outline2ConfigRule,
        configRule2Outline,
    };
};

/**
 * 实施例大纲的配置
 * 用于在UI层面管理实施例大纲的配置，包含实施例的编号、类型和关联的权利要求
 */
export interface EmbodimentOutlineConfigRule {
    /** 实施例编号 */
    embodiment_number: string;
    /** 实施例类型 */
    type: string;
    /** 关联的权利要求列表 */
    claims: SpecificationConfirmClaimItem[];
}

export const useSpecificationEmbodimentOutline = createSharedComposable(() => {
    const service = injectService<SpecificationEmbodimentOutlineService>(SpecificationEmbodimentOutlineService);
    const { model, state, outlines } = useSpecificationEmbodimentOutlines();
    const { outline2ConfigRule, configRule2Outline } = useSpecificationEmbodimentConfigRuleTransform();
    const submitLoading = ref(false);

    /** 原始配置规则，用于比较变更 */
    const orginalConfigRules = ref<EmbodimentOutlineConfigRule[]>([]);

    /** 当前配置规则，用户可以在UI中修改 */
    const configRules = ref<EmbodimentOutlineConfigRule[]>([]);

    /**
     * 监听 outlines 数据变化，同步更新配置规则
     * 当后端数据更新时，自动同步到前端配置规则
     */
    watch(outlines, (to, from) => {
        if (to !== from) {
            orginalConfigRules.value = to.map(outline => outline2ConfigRule(outline));
            configRules.value = to.map(outline => outline2ConfigRule(outline));
        }
    }, { immediate: true });

    /**
     * 计算已修改的配置规则
     * 通过比较原始配置规则和当前配置规则，找出用户修改过的规则
     */
    const modifiedConfigRules = computed(() => {
        return configRules.value.reduce((acc, rule) => {
            const found = orginalConfigRules.value.find(r => r.embodiment_number === rule.embodiment_number);

            // 如果是新增的规则（原始数据中没有）
            if (!found) {
                acc.push(rule);
            }
            // 如果是修改过的规则（与原始数据不同）
            else if (found && !_.isEqual(rule, found)) {
                acc.push(rule);
            }
            return acc;
        }, [] as EmbodimentOutlineConfigRule[]);
    });

    /**
     * 计算下一个可用的实施例编号
     * 基于现有大纲中的最大编号 + 1
     */
    const nextEmbodimentNumber = computed(() => {
        const maxInRule = configRules.value.reduce((max, rule) => {
            return Math.max(max, _.toNumber(rule.embodiment_number) || 0);
        }, 0);

        return maxInRule + 1;
    });

    /**
     * 创建新的配置规则
     * 添加一个新的实施例配置到当前配置列表中
     */
    const createConfigRule = () => {
        configRules.value.push({
            embodiment_number: `${nextEmbodimentNumber.value}`,
            type: '',
            claims: [],
        });
    };

    /**
     * 更新指定索引的配置规则
     * @param index - 要更新的规则索引
     * @param rule - 新的配置规则
     */
    const updateConfigRule = (index: number, rule: EmbodimentOutlineConfigRule) => {
        configRules.value[index] = rule;
        configRules.value = [...configRules.value].sort((a, b) => _.toNumber(a.embodiment_number) - _.toNumber(b.embodiment_number));
    };

    /**
     * 更新大纲数据
     * 将配置规则转换回 EmbodimentOutline 格式并更新模型
     * @param outlines - 新的实施例大纲数据
     */
    const updateOutlines = (outlines: EmbodimentOutline[]) => {
        model.update(data => ({
            ...data,
            outlines,
        }));
    };

    const updateOutline = (outline: EmbodimentOutline) => {
        const index = state.value.outlines.findIndex(o => o.embodiment_number === outline.embodiment_number);
        if (index !== -1) {
            state.value.outlines[index] = outline;
        }
        model.update(data => ({
            ...data,
            outlines: state.value.outlines,
        }));
    };

    const init = () => service.init();

    /**
     * 提交配置
     * 提交后会对已修改的配置规则重新生成大纲，然后进入生成阶段
     */
    const submit = async () => {
        try {
            submitLoading.value = true;
            const finalOutlines = await Promise.all(configRules.value.map(async (rule) => {
                const isModified = modifiedConfigRules.value.includes(rule);
                if (isModified) {
                    const outline = await service.append(configRule2Outline(rule));
                    const newOutline: EmbodimentOutline = {
                        ...outline,
                        type: rule.type,
                        reference_claim_number: [],
                        embodiment_number: _.toNumber(rule.embodiment_number),
                    };
                    return newOutline;
                }
                return state.value.outlines.find(outline => outline.embodiment_number === _.toNumber(rule.embodiment_number));
            }));

            updateOutlines(finalOutlines.filter(Boolean) as EmbodimentOutline[]);

            await service.submit();
        } catch (e) {
            console.error(e);
        } finally {
            submitLoading.value = false;
        }
    };

    const deleteConfigRule = async (rule: EmbodimentOutlineConfigRule) => {
        const index = configRules.value.indexOf(rule);
        if (index >= configRules.value.length - 1) {
            configRules.value.splice(index, 1);
        }
    };

    return {
        state,
        init,
        submit,
        service,
        createConfigRule,
        updateConfigRule,
        configRules,
        submitLoading,
        deleteConfigRule,
    };
});
